import { ZoneContextManager } from '@opentelemetry/context-zone';
import {
  CompositePropagator,
  W3CBaggagePropagator,
  W3CTraceContextPropagator,
} from '@opentelemetry/core';
import { BatchSpanProcessor, WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { getWebAutoInstrumentations } from '@opentelemetry/auto-instrumentations-web';
import { resourceFromAttributes } from '@opentelemetry/resources';
import * as SemanticResourceAttributes from '@opentelemetry/semantic-conventions';
import { SpanStatusCode } from '@opentelemetry/api';

/**
 * Initializes OpenTelemetry tracing for the web application.
 *
 * Configures and registers a WebTracerProvider with the following features:
 * - Sets up OTLP HTTP exporter for sending traces to LightStep
 * - Configures service name based on brand code and deployment environment
 * - Registers web auto-instrumentations for automatic span creation
 * - Sets up context propagation using W3C standards (Baggage and Trace Context)
 * - Applies custom attributes to fetch spans for synthetic request tracking
 *
 * Required environment variables:
 * - VITE_BRAND_CODE: Brand identifier (e.g., 'amana')
 * - VITE_OTEL_SERVICE_VERSION: Service version for resource attributes
 * - VITE_OTEL_EXPORTER_OTLP_ENDPOINT: OTLP exporter endpoint URL
 * - VITE_OTEL_LIGHTSTEP_TOKEN: Authentication token for LightStep
 * - MODE: Deployment environment ('production', 'development', etc.)
 *
 * If any required variables are missing, telemetry initialization is skipped
 * with a warning logged in non-production environments.
 *
 * @async
 * @returns {Promise<void>} Resolves when telemetry initialization is complete or skipped
 * @example
 * ```
 * await tracer();
 * ```
 * @todo Consider adding error handling for exporter failures.
 * @todo Review and update resource attributes as per latest OpenTelemetry specifications.
 * @todo Potentially move to a separate telemetry module for better organization (lib).
 */
export const tracer = async (): Promise<void> => {
  const ignoreUrls = [
    // Anchored to the host boundary (scheme/subdomain separator before, path or
    // end after) so an attacker-controlled host such as
    // "google-analytics.com.evil.example" can't match. Applied to full request URLs.
    /(?:\/\/|\.)google-analytics\.com(?:[/:?#]|$)/, // ignore Google Analytics requests
    /(?:\/\/|\.)lightstep\.com(?:[/:?#]|$)/, // ignore LightStep export requests (incl. subdomains)
  ];

  const {
    VITE_BRAND_CODE,
    VITE_OTEL_SERVICE_VERSION,
    VITE_OTEL_EXPORTER_OTLP_ENDPOINT,
    VITE_OTEL_LIGHTSTEP_TOKEN,
    VITE_BASE_API_URL,
    MODE,
  } = import.meta.env;

  if (
    !VITE_BRAND_CODE ||
    !VITE_OTEL_SERVICE_VERSION ||
    !VITE_OTEL_EXPORTER_OTLP_ENDPOINT ||
    !VITE_OTEL_LIGHTSTEP_TOKEN
  ) {
    if (MODE !== 'production') {
      console.warn(
        '[APP WARNING] OpenTelemetry environment variables are not set correctly. Telemetry will not be initialized.',
      );
    }
    return;
  } else {
    if (MODE !== 'production') {
      console.log('OpenTelemetry environment variables found. Initializing telemetry...');
    }
    const provider = new WebTracerProvider({
      resource: resourceFromAttributes({
        // Properties are deprecated according to latest spec
        [SemanticResourceAttributes.ATTR_SERVICE_NAME]:
          // e.g. olr-lp-candy-eu-client-development in non-prod; olr-lp-candy-eu-client in production.
          // `olr-lp` is the EU estate's appName and the region is eu (this was inherited as `olr-<brand>-us-client` from the US repo).
          `olr-lp-${VITE_BRAND_CODE.toLocaleLowerCase()}-eu-client${MODE !== 'production' ? `-${MODE}` : ''}`.trim(),
        [SemanticResourceAttributes.SEMRESATTRS_DEPLOYMENT_ENVIRONMENT]: MODE,
        [SemanticResourceAttributes.ATTR_SERVICE_VERSION]: VITE_OTEL_SERVICE_VERSION, // we should use the package version here
      }),
      spanProcessors: [
        new BatchSpanProcessor(
          new OTLPTraceExporter({
            url: import.meta.env.VITE_OTEL_EXPORTER_OTLP_ENDPOINT,
            headers: {
              ['lightstep-access-token']: VITE_OTEL_LIGHTSTEP_TOKEN,
            },
          }),
        ),
      ],
    });

    const contextManager = new ZoneContextManager();

    provider.register({
      contextManager,
      propagator: new CompositePropagator({
        propagators: [new W3CBaggagePropagator(), new W3CTraceContextPropagator()],
      }),
    });

    registerInstrumentations({
      tracerProvider: provider,
      instrumentations: [
        getWebAutoInstrumentations({
          '@opentelemetry/instrumentation-fetch': {
            enabled: true,
            propagateTraceHeaderCorsUrls: [new RegExp(VITE_BASE_API_URL)], // The urls to propagate the 'traceparent' headers to
            clearTimingResources: true,
            ignoreUrls,
            applyCustomAttributesOnSpan: (span, _, response) => {
              if (response.status && [400, 404, 500, 502, 503, 504].includes(response.status)) {
                span.setStatus({ code: SpanStatusCode.ERROR });
                span.setAttribute('http.status_code', response.status);
              }
            },
          },
          /** https://github.com/open-telemetry/opentelemetry-js-contrib/tree/main/packages/instrumentation-document-load */
          '@opentelemetry/instrumentation-document-load': {
            enabled: true,
          },
          /** https://github.com/open-telemetry/opentelemetry-js-contrib/tree/main/packages/instrumentation-user-interaction */
          '@opentelemetry/instrumentation-user-interaction': {
            enabled: true,
            // Restrict to high-signal events to balance visibility and overhead:
            // - 'click' and 'submit' capture primary user interactions with the UI
            // - 'load' and 'error' capture key lifecycle and failure events
            // This deviates from the broader default event set to avoid noisy spans
            // while still providing actionable interaction telemetry.
            eventNames: ['click', 'submit', 'load', 'error'],
          },
          '@opentelemetry/instrumentation-xml-http-request': {
            enabled: true,
            propagateTraceHeaderCorsUrls: [new RegExp(VITE_BASE_API_URL)],
            ignoreUrls,
            clearTimingResources: true,
          },
        }),
      ],
    });
  }
};
